import { useEffect } from 'react';
import { api } from '../lib/api';
import { useStore } from '../state/store';

export function Toolbar() {
  const { current, ingestLine, setView, view, showVoided, setShowVoided, filter, setFilter } =
    useStore((s) => ({
      current: s.current,
      ingestLine: s.ingestLine,
      setView: s.setView,
      view: s.view,
      showVoided: s.showVoided,
      setShowVoided: s.setShowVoided,
      filter: s.filter,
      setFilter: s.setFilter,
    }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z') {
        // While typing, Cmd/Ctrl+Z must stay the browser's own undo —
        // voiding a row because someone undid a typo is a disaster.
        const t = document.activeElement;
        const tag = t?.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (t instanceof HTMLElement && t.isContentEditable) return;
        e.preventDefault();
        void ingestLine('void last');
      } else if (key === 'r') {
        e.preventDefault();
        setView('review');
      } else if (key === 'e') {
        e.preventDefault();
        void doExport();
      } else if (key === 's') {
        e.preventDefault();
        // Auto-save is implicit; show a toast.
        useStore.getState().toast('success', 'All edits already saved');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ingestLine, setView]);

  async function doExport() {
    const cur = current;
    if (!cur) return;
    const fmt: 'xlsx' = 'xlsx';
    // Browser path: no native save-dialog round-trip; the file is
    // streamed back from the server and triggerDownload presents the
    // browser's normal download UI. The destinationPath argument is
    // unused in the web build.
    try {
      await api.exportSession(cur.id, {
        mode: 'clean',
        format: fmt,
        destinationPath: '',
      });
      useStore.getState().toast('success', `Exported as ${cur.name} - clean.${fmt}`);
    } catch (err) {
      useStore.getState().toast('error', `Export failed: ${(err as Error).message}`);
    }
  }

  if (!current) return null;
  return (
    <div className="toolbar">
      <div className="toolbar-group">
        <button onClick={() => void ingestLine('void last')} title="Ctrl/Cmd+Z">Void last</button>
        <button onClick={() => void ingestLine('restore last void')}>Restore last void</button>
        <button onClick={() => void ingestLine('check last')}>Check last</button>
        <button onClick={() => void ingestLine('uncheck last')}>Uncheck last</button>
      </div>
      <div className="toolbar-group">
        <label>
          <input
            type="checkbox"
            checked={showVoided}
            onChange={(e) => setShowVoided(e.target.checked)}
          /> Show voided
        </label>
        <label>
          Filter
          <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="all">All</option>
            <option value="checked">Checked</option>
            <option value="amended">Amended</option>
            <option value="warning">With warnings</option>
            <option value="void">Voided</option>
          </select>
        </label>
      </div>
      <div className="toolbar-group">
        <button onClick={() => setView('capture')} aria-current={view === 'capture'}>Capture</button>
        <button onClick={() => setView('review')} aria-current={view === 'review'} title="Ctrl/Cmd+R">Review queue</button>
        <button onClick={() => setView('settings')} aria-current={view === 'settings'}>Settings</button>
        <button onClick={() => setView('guide')} aria-current={view === 'guide'}>Guide</button>
        <button onClick={() => void doExport()} title="Ctrl/Cmd+E">Export…</button>
        <ExportMenu />
        <ImportAudioButton />
      </div>
    </div>
  );
}

function ImportAudioButton() {
  const { settings, transcribeAudioFile, toast } = useStore((s) => ({
    settings: s.settings,
    transcribeAudioFile: s.transcribeAudioFile,
    toast: s.toast,
  }));
  if (!settings || settings.transcriptionProvider === 'manual' || !settings.cloudPrivacyAcknowledged) {
    return null;
  }
  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    toast('info', `Transcribing ${file.name}…`);
    void transcribeAudioFile('openai', file);
    e.target.value = '';
  }
  return (
    <label className="import-audio">
      <span>Import audio…</span>
      <input
        type="file"
        accept="audio/mp3,audio/mpeg,audio/wav,audio/m4a,audio/mp4,audio/webm,audio/ogg,audio/flac"
        onChange={onFile}
        hidden
      />
    </label>
  );
}

function ExportMenu() {
  const current = useStore((s) => s.current);
  if (!current) return null;
  async function exportAs(mode: 'clean' | 'audit' | 'review', format: 'csv' | 'xlsx') {
    const cur = useStore.getState().current!;
    try {
      await api.exportSession(cur.id, { mode, format, destinationPath: '' });
      useStore.getState().toast('success', `Exported as ${cur.name} - ${mode}.${format}`);
    } catch (err) {
      useStore.getState().toast('error', `Export failed: ${(err as Error).message}`);
    }
  }
  function pick(
    e: React.MouseEvent<HTMLButtonElement>,
    mode: 'clean' | 'audit' | 'review',
    format: 'csv' | 'xlsx',
  ) {
    // <details> stays open after a click; close it so the menu doesn't
    // linger over the toolbar after a choice.
    e.currentTarget.closest('details')?.removeAttribute('open');
    void exportAs(mode, format);
  }
  return (
    <details className="export-menu">
      <summary>More exports…</summary>
      <button onClick={(e) => pick(e, 'clean', 'csv')}>Clean CSV</button>
      <button onClick={(e) => pick(e, 'clean', 'xlsx')}>Clean XLSX</button>
      <button onClick={(e) => pick(e, 'audit', 'csv')}>Audit CSV</button>
      <button onClick={(e) => pick(e, 'audit', 'xlsx')}>Audit XLSX</button>
      <button onClick={(e) => pick(e, 'review', 'csv')}>Review CSV</button>
      <button onClick={(e) => pick(e, 'review', 'xlsx')}>Review XLSX</button>
    </details>
  );
}
